import { initializeApp, getApps, FirebaseApp } from 'firebase/app';
import { getAuth, initializeAuth, browserSessionPersistence, Auth } from 'firebase/auth';
import { getFirestore, Firestore } from 'firebase/firestore';
import { getStorage, FirebaseStorage } from 'firebase/storage';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

export const isFirebaseConfigured = Boolean(firebaseConfig.apiKey);

// ───────────────────────────────────────────────────────────────────────
// AUTH ISOLATION INVARIANT — DO NOT BREAK
//
// The customer portal and admin dashboard run in the same browser and
// must not leak auth state across each other. A customer signing in must
// not log the admin out, and vice-versa. We enforce this with:
//
//   1. Two Firebase apps: the default (customer) and a named "admin-app".
//   2. Admin auth uses `browserSessionPersistence` (tab-scoped) while
//      customer auth uses the default IndexedDB persistence. IndexedDB
//      auth state is broadcast across instances sharing the same API key,
//      so giving admin a different persistence is what keeps the two
//      sessions from colliding.
//   3. Every consumer must use the correct accessor:
//        - customer pages → `auth` (or `useCustomerAuth`)
//        - admin pages → `adminAuth` (or `useAuth`)
//      Importing the wrong one silently re-introduces the cross-logout bug.
//
// If you change persistence, the app name, or the accessor pattern, audit
// every caller of `useAuth` / `useCustomerAuth` / `auth` / `adminAuth`
// before merging. The previous bug that prompted this guard: admin login
// logged the TestGym customer out and vice-versa (fixed 2026-04 per
// memory note `feedback_firebase_auth_isolation.md`).
// ───────────────────────────────────────────────────────────────────────

const CUSTOMER_APP_NAME = '[DEFAULT]';
const ADMIN_APP_NAME = 'admin-app';

let app: FirebaseApp | null = null;
let adminApp: FirebaseApp | null = null;
let auth: Auth | null = null;
let adminAuth: Auth | null = null;
let db: Firestore | null = null;
let storage: FirebaseStorage | null = null;

if (isFirebaseConfigured) {
  if (!getApps().length) {
    app = initializeApp(firebaseConfig);
  } else {
    app = getApps().find(a => a.name === CUSTOMER_APP_NAME) || getApps()[0];
  }

  try {
    adminApp = initializeApp(firebaseConfig, ADMIN_APP_NAME);
  } catch {
    adminApp = getApps().find(a => a.name === ADMIN_APP_NAME) || null;
  }

  auth = getAuth(app);
  if (adminApp) {
    try {
      adminAuth = initializeAuth(adminApp, { persistence: browserSessionPersistence });
    } catch {
      adminAuth = getAuth(adminApp);
    }
  }
  db = getFirestore(app);
  storage = getStorage(app);

  // Runtime invariant — fail loud in development if the two auth instances
  // were ever wired up to the same FirebaseApp. This would silently break
  // session isolation.
  if (import.meta.env.DEV && auth && adminAuth && auth.app.name === adminAuth.app.name) {
    // eslint-disable-next-line no-console
    console.error(
      '[firebase/config] AUTH ISOLATION VIOLATION — customer and admin auth share the same FirebaseApp. ' +
      'Review src/lib/firebase/config.ts before shipping.'
    );
  }
}

export { app, adminApp, auth, adminAuth, db, storage };
